import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import {
  getPermissionMatrix,
  setRolePermissions,
  type PermissionMatrix,
  type RoleDetail,
} from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import { cn } from '@/lib/cn'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

/**
 * The PRD §9 matrix, as a screen you can edit.
 *
 * Until M7 this grid was a constant in two codebases and changing it meant a deploy. It is
 * rows in `role_permissions` now, and the guard reads them per request, so a change here
 * binds on the affected user's very next call rather than when their token expires.
 *
 * Two things the screen is careful about. Super Admin is not editable -- it is the way back
 * in if somebody revokes `roles:update` from everything else, and a grid that can lock
 * every admin out of the grid is a grid nobody should be given. And a save replaces a
 * role's permissions whole rather than sending the difference, so two people editing this
 * page land on one of their two answers instead of the union of both.
 */

/** role slug -> resource -> the actions that role holds on it. */
type Draft = Record<string, Record<string, string[]>>

function draftOf(matrix: PermissionMatrix): Draft {
  const draft: Draft = {}
  for (const role of matrix.roles) {
    draft[role.slug] = {}
    for (const row of matrix.resources) {
      draft[role.slug][row.resource] = row.actions[role.slug] ?? []
    }
  }
  return draft
}

/** `{ invoices: ['create','read'] }` -> `['invoices:create','invoices:read']`, sorted. */
function codesOf(held: Record<string, string[]>): string[] {
  return Object.entries(held)
    .flatMap(([resource, actions]) => actions.map((action) => `${resource}:${action}`))
    .sort()
}

/** The legend letter a set of actions matches exactly, or `null` for a custom set. */
function letterOf(actions: string[], legendActions: Record<string, string[]>): string | null {
  const wanted = [...actions].sort().join(',')
  for (const [letter, granted] of Object.entries(legendActions)) {
    if ([...granted].sort().join(',') === wanted) return letter
  }
  return null
}

const CELL_TONE: Record<string, string> = {
  F: 'text-brand-700 dark:text-brand-300',
  V: 'text-slate-600 dark:text-slate-300',
  '-': 'text-slate-300 dark:text-slate-600',
}

export function Roles() {
  const can = useAuth((s) => s.can)
  const reloadMe = useAuth((s) => s.reload)
  const canEdit = can('roles', 'update')
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['roles', 'matrix'],
    queryFn: getPermissionMatrix,
    retry: false,
  })
  const matrix = query.data

  const [draft, setDraft] = useState<Draft>({})
  const [formError, setFormError] = useState<string | null>(null)

  // Reset whenever the server's copy changes -- on first load, and again after a save
  // invalidates it. Nothing refetches this query in the background, so an edit in progress
  // is not at risk of being overwritten mid-change.
  useEffect(() => {
    if (matrix) setDraft(draftOf(matrix))
  }, [matrix])

  const changed = useMemo(() => {
    if (!matrix) return [] as RoleDetail[]
    return matrix.roles.filter((role) => {
      const held = draft[role.slug]
      if (!held) return false
      return codesOf(held).join('|') !== [...role.permissions].sort().join('|')
    })
  }, [matrix, draft])

  const guard = useDirtyGuard(changed.length > 0)

  const save = useMutation({
    mutationFn: async (roles: RoleDetail[]) => {
      // One request per role, in sequence: the API has no bulk route, and a failure part
      // way through leaves the roles before it genuinely saved rather than half-applied.
      for (const role of roles) await setRolePermissions(role.id, codesOf(draft[role.slug]))
      return roles.length
    },
    onSuccess: async (count) => {
      setFormError(null)
      await queryClient.invalidateQueries({ queryKey: ['roles'] })
      // The editor may have just changed their own access. Re-read it so the navigation
      // and every other screen agree with what the server will now enforce.
      await reloadMe()
      toast.success(`${count} role${count === 1 ? '' : 's'} updated.`)
    },
    onError: (error: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] })
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing further was changed.',
      )
    },
  })

  const setCell = (roleSlug: string, resource: string, letter: string) => {
    const granted = matrix?.legend_actions[letter] ?? []
    setDraft((current) => ({
      ...current,
      [roleSlug]: { ...current[roleSlug], [resource]: granted },
    }))
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Roles &amp; permissions</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {canEdit
              ? 'A change here applies on that person’s next action — nobody signs out and in.'
              : 'What each role may do. Editing it needs roles:update.'}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={changed.length === 0 || save.isPending}
              onClick={() => matrix && setDraft(draftOf(matrix))}
            >
              Reset
            </Button>
            <Button
              disabled={changed.length === 0}
              loading={save.isPending}
              onClick={() => save.mutate(changed)}
            >
              {changed.length === 0
                ? 'Save changes'
                : `Save ${changed.length} role${changed.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}
      </header>

      {formError && <Alert tone="error">{formError}</Alert>}

      <Card>
        <CardHeader
          title="Permission matrix"
          description="Read from the database, not from a constant. Rows are resources, columns are roles."
        />
        <QueryState isLoading={query.isLoading} error={query.error}>
          {matrix && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[64rem] border-collapse text-sm">
                <caption className="sr-only">Permission matrix</caption>
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th
                      scope="col"
                      className="sticky left-0 z-10 bg-white px-5 py-3 text-left font-medium dark:bg-slate-900"
                    >
                      Resource
                    </th>
                    {matrix.roles.map((role) => (
                      <th
                        key={role.slug}
                        scope="col"
                        className="px-2 py-3 text-center align-bottom font-medium"
                      >
                        <span className="block">{role.name}</span>
                        <span className="block text-xs font-normal text-slate-400">
                          {role.user_count} user{role.user_count === 1 ? '' : 's'}
                          {!role.editable && ' · locked'}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.resources.map((row) => (
                    <tr
                      key={row.resource}
                      className="border-b border-slate-100 last:border-0 dark:border-slate-800/60"
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-white px-5 py-2 text-left font-normal dark:bg-slate-900"
                      >
                        {row.label}
                        <span className="block text-xs text-slate-400">{row.resource}</span>
                      </th>
                      {matrix.roles.map((role) => {
                        const actions = draft[role.slug]?.[row.resource] ?? []
                        const letter = letterOf(actions, matrix.legend_actions)
                        return (
                          <td key={role.slug} className="px-2 py-2 text-center">
                            {canEdit && role.editable ? (
                              <select
                                aria-label={`${row.label} for ${role.name}`}
                                value={letter ?? 'custom'}
                                onChange={(event) =>
                                  setCell(role.slug, row.resource, event.target.value)
                                }
                                className={cn(
                                  'h-8 w-16 rounded-md border border-slate-300 bg-white text-center',
                                  'text-sm focus:border-brand-500 focus:outline-none',
                                  'focus:ring-1 focus:ring-brand-500',
                                  'dark:border-slate-700 dark:bg-slate-900',
                                  CELL_TONE[letter ?? ''] ?? 'text-slate-700 dark:text-slate-200',
                                )}
                              >
                                {letter === null && (
                                  // Only ever a starting value: a combination the legend
                                  // cannot name, which the server still enforces.
                                  <option value="custom">Custom</option>
                                )}
                                {Object.entries(matrix.legend).map(([code, name]) => (
                                  <option key={code} value={code}>
                                    {code === '-' ? '—' : `${code} · ${name}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span
                                title={letter ? matrix.legend[letter] : actions.join(', ')}
                                className={cn(
                                  'font-mono',
                                  CELL_TONE[letter ?? ''] ?? 'text-slate-700 dark:text-slate-200',
                                )}
                              >
                                {letter === '-' ? '—' : (letter ?? '•')}
                              </span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
      </Card>

      {matrix && (
        <Card>
          <CardHeader title="Legend" description="What each letter grants." />
          <CardBody className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {Object.entries(matrix.legend).map(([code, name]) => (
              <span key={code} className="text-slate-600 dark:text-slate-300">
                <span className="font-mono font-semibold">{code === '-' ? '—' : code}</span> {name}
                <span className="text-slate-400">
                  {' '}
                  ({matrix.legend_actions[code]?.join(', ') || 'nothing'})
                </span>
              </span>
            ))}
          </CardBody>
        </Card>
      )}

      <UnsavedChangesDialog guard={guard} />
    </div>
  )
}
